import React, { useEffect } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { saveToken } from '../../redux/ducks/auth';
import { alert, warning } from '../../redux/ducks/notification';
import useForm from '../../hooks/use-form';
import { login, decodeToken, isAdmin } from '../../services/auth/auth-service';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import LockOpenIcon from '@material-ui/icons/LockOpen';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '70vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: 450,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3),
    },
  },
  iconContainer: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  icon: {
    fontSize: 40,
    color: theme.palette.primary.contrastText,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    padding: theme.spacing(1.5),
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
}));

const Login = ({ history }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const role = useSelector(state => state.auth.role);
  const loading = useSelector(state => state.request.loading);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isAdmin(role)) {
      history.push('/dashboard/main');
    }
  }, []); // eslint-disable-line

  const stateSchema = {
    email: { value: '', error: '' },
    password: { value: '', error: '' },
  };

  const stateValidatorSchema = {
    email: {
      required: true,
      validator: {
        func: value => /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value),
        error: t('login.form.validation.email'),
      },
    },
    password: {
      required: true,
      validator: {
        func: value => /^.{6,20}$/.test(value),
        error: t('login.form.validation.password'),
      },
    },
  };

  const onSubmitForm = async () => {
    try {
      const response = await login(email, password);
      const token = response.data.token;
      const payload = decodeToken(token);
      dispatch(saveToken(token, payload));
      history.push('/dashboard/main')
    } catch (e) {
      if (undefined === e.response) {
        dispatch(alert(t('errors.common.no_connection')));
        return;
      }

      if (401 === e.response.status) {
        dispatch(warning(t('errors.login.invalid_credentials')));
        return;
      }

      if (409 === e.response.status) {
        dispatch(alert(t('errors.login.inactive_account')));
        return;
      }

      dispatch(alert(t('errors.common.server')));
    }
  }

  const {
    values,
    errors,
    dirty,
    handleOnChange,
    handleOnSubmit,
    disable,
  } = useForm(stateSchema, stateValidatorSchema, onSubmitForm);

  const { email, password } = values;

  return (
    <div className={classes.root}>
      <Container maxWidth="sm">
        <Paper className={classes.paper} elevation={2}>
          <Box className={classes.iconContainer}>
            <LockOpenIcon className={classes.icon} />
          </Box>
          <Typography component="h1" variant="h4" className={classes.title}>
            {t('app_name')}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            {t('company_name')}
          </Typography>
          <form
            className={classes.form}
            onSubmit={handleOnSubmit}
            noValidate
          >
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              label={t('login.form.email')}
              name="email"
              value={email}
              onChange={handleOnChange}
              autoComplete="email"
              autoFocus
              error={errors.email.length > 0 && dirty.email}
              helperText={errors.email}
              size="small"
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('login.form.password')}
              value={password}
              onChange={handleOnChange}
              type="password"
              id="password"
              autoComplete="current-password"
              error={errors.password.length > 0 && dirty.password}
              helperText={errors.password}
              size="small"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={disable || loading}
              size="large"
            >
              {t('login.form.button.login')}
            </Button>
          </form>
        </Paper>
      </Container>
    </div>
  );
};

export default Login;
